import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Store, Power, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { euro } from "@/lib/i18n";

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const load = async () => {
    const [s, r, p] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/restaurants"),
      api.get("/plans"),
    ]);
    setStats(s.data);
    setRestaurants(r.data);
    setPlans(p.data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (rest) => {
    try {
      await api.put(`/admin/restaurants/${rest.id}/status`, null, { params: { active: !rest.active } });
      await load();
      toast.success("Aggiornato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doLogout = async () => { await logout(); nav("/"); };

  const changePassword = async () => {
    if (pw.next.length < 6) return toast.error("Minimo 6 caratteri");
    if (pw.next !== pw.confirm) return toast.error("Le password non coincidono");
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password aggiornata");
      setPwOpen(false);
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setPwLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="font-display text-2xl font-black" data-testid="admin-brand-link">
            Tavolo<span className="text-[hsl(var(--accent))]">OS</span> <span className="text-sm font-normal opacity-60">/ admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" onClick={() => setPwOpen(true)} data-testid="admin-change-password-btn" title="Cambia password">
              <KeyRound className="h-4 w-4" />
            </Button>
            <Button variant="ghost" onClick={doLogout} data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="admin-pw-dialog">
          <DialogHeader><DialogTitle>Cambia password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Password attuale</Label>
              <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="admin-pw-current" />
            </div>
            <div>
              <Label>Nuova password</Label>
              <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="admin-pw-next" />
            </div>
            <div>
              <Label>Conferma nuova password</Label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="admin-pw-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwOpen(false)}>Annulla</Button>
            <Button onClick={changePassword} disabled={pwLoading} className="pill-btn bg-primary text-primary-foreground" data-testid="admin-pw-save">
              {pwLoading ? "…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-10" data-testid="admin-page">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Piattaforma</div>
          <h1 className="font-display text-5xl font-black tracking-tighter mt-1">Console</h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {stats && [
            ["Ristoranti", stats.total_restaurants],
            ["Attivi", stats.active_restaurants],
            ["Abbonamenti", stats.active_subscriptions],
            ["Ordini totali", stats.total_orders],
            ["GMV pagato", euro(stats.total_gmv, "EUR")],
          ].map(([l, v], i) => (
            <div key={i} className="p-5 bg-card border border-border rounded-2xl" data-testid={`admin-stat-${i}`}>
              <div className="font-display text-3xl font-bold">{v}</div>
              <div className="text-xs text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>

        <section>
          <h2 className="font-display text-2xl font-bold mb-4">Ristoranti</h2>
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Ristorante</th>
                  <th className="text-left px-4 py-3">Owner</th>
                  <th className="text-left px-4 py-3">Piano</th>
                  <th className="text-left px-4 py-3">Stato Abb.</th>
                  <th className="text-left px-4 py-3">Attivo</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {restaurants.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`admin-rest-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium flex items-center gap-2"><Store className="h-4 w-4 text-muted-foreground" /> {r.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.owner_email}</td>
                    <td className="px-4 py-3">{r.subscription_plan || "—"}</td>
                    <td className="px-4 py-3">{r.subscription_status}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${r.active ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                        {r.active ? "Attivo" : "Sospeso"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => toggle(r)} data-testid={`admin-toggle-${r.id}`}>
                        <Power className="h-3 w-3 mr-1" /> {r.active ? "Sospendi" : "Riattiva"}
                      </Button>
                    </td>
                  </tr>
                ))}
                {restaurants.length === 0 && (
                  <tr><td colSpan="6" className="p-12 text-center text-muted-foreground">Nessun ristorante.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold mb-4">Piani SaaS</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.id} className="p-6 bg-card border border-border rounded-2xl" data-testid={`admin-plan-${p.lookup_key}`}>
                <div className="font-display text-xl font-bold">{p.name}</div>
                <div className="font-display text-3xl mt-2">€{(p.price_cents / 100).toFixed(0)}<span className="text-sm text-muted-foreground">/mese</span></div>
                <div className="text-xs text-muted-foreground mt-2">{p.lookup_key}</div>
                <ul className="mt-3 text-sm space-y-1">
                  {p.features.map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
